import React, { useState, useEffect } from 'react';

// 1. CHỈ IMPORT TĨNH CÁC COMPONENT CẦN THIẾT (Tối ưu Tree-shaking)
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import ContainerGrid from "./ContainerGrid";//Sử dụng Grid này thay cho Grid của MUI để nó response theo container thay vì màn hình thiết bị
import {loadBabelOnce} from "../utils";
import { useQuickAction } from "./DataQuickActionContext";//Tạo nhanh chứng từ khác (openAdd/openEdit/openView) từ trong script
import { bienDichComponent } from './bienDichJsx';//Biên dịch script JSX -> component (an toàn với hàm phụ Babel chèn đầu mã)

// =====================================================================
// COMPONENT LÕI: TRÌNH BIÊN DỊCH GIAO DIỆN VIEW ĐỘNG (RUNTIME COMPILER)
// =====================================================================
const DynamicCustomView = ({ script, item, onBack, onEdit, onDelete, onCopy, formatField, context, rowActions }) => {
  // quickAction: dùng DataQuickActionContext (KHÔNG dùng context.quickAdd) để tạo nhanh chứng từ tiếp theo
  const quickAction = useQuickAction();
  // Thêm MUI vào state để lưu trữ module sau khi tải động thành công
  const [CompilerConfig, setCompilerConfig] = useState({ Component: null, MUI: null, error: null, isLoading: true });

  useEffect(() => {
    let isMounted = true;
    
    const compileScript = async () => {
      try {
        setCompilerConfig(prev => ({ ...prev, isLoading: true, error: null }));

        // 2. TẢI ĐỘNG (LAZY-LOAD) BABEL VÀ MUI CÙNG LÚC
        const loadBabel = loadBabelOnce();
        const [Babel, DynamicallyLoadedMUI] = await Promise.all([
          loadBabel,
          import('@mui/material') // Tách MUI thành chunk riêng
        ]);

        // 3. PHÂN TÍCH SCRIPT CỦA USER VÀ KIỂM TRA TÍNH TƯƠNG THÍCH
        let scriptBody = script;
        if (!scriptBody.includes("return ")) {
            scriptBody = "return (\n<React.Fragment>\n" + scriptBody + "\n</React.Fragment>\n);";
        }

        // 4. BỌC SCRIPT THÀNH REACT FUNCTIONAL COMPONENT
        const wrappedScript = 
          "(function UserCustomView(props) {\n" +
          "  const { MUI, React, item, onBack, onEdit, onDelete, onCopy, formatField, context, quickAction, rowActions } = props;\n" +
          "  " + scriptBody + "\n" +
          "})";

        // 6. DỊCH JSX + KHỞI TẠO COMPONENT (xem bienDichJsx: không ghép `return ${ma}`)
        const UserComponent = bienDichComponent(Babel, wrappedScript, ['React', 'MUI', 'ContainerGrid'], [React, DynamicallyLoadedMUI, ContainerGrid]);

        if (isMounted) {
            setCompilerConfig({ 
                Component: UserComponent, 
                MUI: DynamicallyLoadedMUI, 
                error: null, 
                ContainerGrid,
                isLoading: false 
            });
        }
      } catch (err) {
        console.error("Lỗi biên dịch Custom View:\n", script);
        if (isMounted) setCompilerConfig({ Component: null, MUI: null, error: err.message, isLoading: false });
      }
    };

    if (script) {
        compileScript();
    } else {
        if (isMounted) setCompilerConfig({ Component: null, MUI: null, error: null, isLoading: false });
    }
    
    return () => { isMounted = false; };
  }, [script]);

  if (CompilerConfig.isLoading) return (
    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center', p: 4 }}>
      <CircularProgress size={24} />
      <Typography color="text.secondary">Đang tải tài nguyên và biên dịch giao diện...</Typography>
    </Box>
  );

  if (CompilerConfig.error) return (
    <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>
      <strong>Lỗi biên dịch Giao diện View:</strong> {CompilerConfig.error}
    </Alert>
  );

  const { Component, MUI } = CompilerConfig;
  if (!Component || !MUI) return null;

  return (
    <Component 
      item={item} 
      onBack={onBack} 
      onEdit={onEdit} 
      onDelete={onDelete} 
      onCopy={onCopy} 
      formatField={formatField}
      context={context}
      quickAction={quickAction}
      rowActions={rowActions}
      MUI={MUI}
      ContainerGrid={ContainerGrid}
      React={React}
    />
  );
};

export default DynamicCustomView;